import { useEffect, useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { getAvatarInitials, resolveAvatarUrl } from '@/utils/resolveAvatarUrl'

const SIZE_CLASSES = {
  xs: 'h-7 w-7 text-xs',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-14 w-14 text-base',
  lgPlus: 'h-20 w-20 text-lg',
  profile: 'h-[88px] w-[88px] text-xl',
  xl: 'h-24 w-24 text-xl',
} as const

export type UserProfileAvatarSize = keyof typeof SIZE_CLASSES

type ImageLoadState = 'idle' | 'loading' | 'loaded' | 'error'

export interface UserProfileAvatarProps {
  avatar?: string | null
  firstName?: string | null
  lastName?: string | null
  name?: string | null
  userId?: string | null
  size?: UserProfileAvatarSize
  className?: string
  fallbackClassName?: string
}

export function UserProfileAvatar({
  avatar,
  firstName,
  lastName,
  name,
  userId,
  size = 'md',
  className,
  fallbackClassName,
}: UserProfileAvatarProps) {
  const avatarSrc = resolveAvatarUrl(avatar)
  const [imageState, setImageState] = useState<ImageLoadState>('idle')
  const initials = getAvatarInitials(firstName, lastName, name)
  const displayName = name ?? [firstName, lastName].filter(Boolean).join(' ').trim()

  useEffect(() => {
    if (!avatarSrc) {
      setImageState('idle')
      return
    }

    setImageState('loading')
    const img = new Image()
    img.onload = () => setImageState('loaded')
    img.onerror = () => setImageState('error')
    img.src = avatarSrc
    if (img.complete && img.naturalWidth > 0) {
      setImageState('loaded')
    }
  }, [avatarSrc, userId])

  const showImage = Boolean(avatarSrc && imageState === 'loaded')
  const showFallback = !showImage

  return (
    <Avatar
      key={userId ?? avatarSrc ?? 'no-avatar'}
      className={cn(SIZE_CLASSES[size], className)}
    >
      {showImage ? (
        <AvatarImage src={avatarSrc!} alt={displayName || initials} className="object-cover" />
      ) : null}
      {showFallback ? (
        <AvatarFallback className={cn('font-medium', fallbackClassName)}>{initials}</AvatarFallback>
      ) : null}
    </Avatar>
  )
}
